import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import { Category } from '@ui/src/components/accounts-selector/types';
import {
  GeneralLedgerTransaction,
  Response,
  AccountBalance,
  AccountType,
  Document,
  Pagination,
  DocumentComment,
  DocumentStatus,
  DocumentType,
} from '@ui/src';
import { GeneralLedgerTransactionValidator } from '../../types';
import { glTransactionsListApi } from '../services';
import { Balance } from '../../../../types/account';
import { transactionApi } from '../../../transactions/store/services';

export interface GeneralLedgerTransactionState {
  panelIsOpen: boolean;
  transactionId?: string;
  transaction?: GeneralLedgerTransaction;
  debitAccountBalance?: AccountBalance;
  creditAccountBalance?: AccountBalance;
  validators?: GeneralLedgerTransactionValidator;
  accountCategories?: Category[];
  debitBalances?: Balance[];
  creditBalances?: Balance[];
  documents?: Document[];
  documentsPagination?: Pagination;
  document?: Document;
}

const initialState: GeneralLedgerTransactionState = {
  panelIsOpen: true,
};

export const generalLedgerTransactionSlice = createSlice({
  name: 'generalLedgerTransaction',
  initialState,
  reducers: {
    changePanelState: (
      state,
      action: PayloadAction<{ state: boolean; transactionId?: string }>,
    ) => {
      state.panelIsOpen = action.payload.state;

      if (action.payload.state) {
        state.transactionId = action.payload.transactionId;
      } else {
        state.transactionId = undefined;
        state.transaction = initialState.transaction;
      }
    },
    clearAccountBalance: (state, action: PayloadAction<AccountType>) => {
      if (action.payload === AccountType.DEBIT) {
        state.debitAccountBalance = undefined;
      } else if (action.payload === AccountType.CREDIT) {
        state.creditAccountBalance = undefined;
      }
    },
    clearGeneralLedgerTransactionState: (state) => {
      state.debitBalances = undefined;
      state.creditBalances = undefined;
      state.transactionId = undefined;
      state.transaction = undefined;
      state.panelIsOpen = false;
      state.debitAccountBalance = undefined;
      state.creditAccountBalance = undefined;
      state.documents = undefined;
      state.documentsPagination = undefined;
      state.document = undefined;
    },
    clearDocumentState: (state) => {
      state.document = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        glTransactionsListApi.endpoints.getTransactionsAccountBalanceById
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<AccountBalance>> & {
            meta: {
              arg: {
                originalArgs: {
                  id: string;
                  query?: string | undefined;
                  typeAcc: AccountType;
                };
              };
            };
          },
        ) => {
          if (action.meta.arg.originalArgs.typeAcc) {
            const [accBalance] = action.payload.data.edges.map(
              (item) => item.node,
            );

            if (action.meta.arg.originalArgs.typeAcc === AccountType.DEBIT) {
              state.debitAccountBalance = accBalance;
            } else if (
              action.meta.arg.originalArgs.typeAcc === AccountType.CREDIT
            ) {
              state.creditAccountBalance = accBalance;
            }
          }
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.getGeneralTransactionById
          .matchFulfilled,
        (state, action: PayloadAction<{ data: GeneralLedgerTransaction }>) => {
          const prevTransaction = state.transaction;
          const nextTransaction = action.payload.data;

          if (!prevTransaction) {
            state.transaction = nextTransaction;
          } else if (!isEqual(prevTransaction, nextTransaction)) {
            state.transaction = nextTransaction;
          }
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.getGLTransactionValidators
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: GeneralLedgerTransactionValidator }>,
        ) => {
          state.validators = action.payload.data;
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.getTransactionsAccountsCategories
          .matchFulfilled,
        (state, action: PayloadAction<Response<Category>>) => {
          state.accountCategories = action.payload.data.edges.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.getGLDocuments.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<Document>> & {
            meta: {
              arg: {
                originalArgs: {
                  cursor?: string;
                };
              };
            };
          },
        ) => {
          let documents;

          if (!action.meta?.arg?.originalArgs?.cursor) {
            documents = action.payload.data.edges?.map((item) => item.node);
          } else {
            documents = ([] as Document[]).concat(
              state.documents as Document[],
            );

            action.payload.data.edges?.forEach((item) =>
              documents.push(item.node),
            );
          }

          state.documents = documents;
          state.documentsPagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.addDocumentToGLTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: Document }>) => {
          let nextDocuments: Document[] = [];

          if (state.documents) {
            nextDocuments = ([] as Document[]).concat(
              state.documents,
            );
          }

          nextDocuments.unshift(action.payload.data);

          state.documents = nextDocuments;
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.getAllCreditAccountBalances
          .matchFulfilled,
        (state, action: PayloadAction<Response<Balance>>) => {
          state.creditBalances = action.payload.data.edges.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.getAllDebitAccountBalances
          .matchFulfilled,
        (state, action: PayloadAction<Response<Balance>>) => {
          state.debitBalances = action.payload.data.edges.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.getGLDocument.matchFulfilled,
        (state, action: PayloadAction<{ data: Document }>) => {
          const nextDocument = { ...action.payload.data };

          state.document = {
            ...nextDocument,
            commentsList: action.payload.data.comments?.edges.map(
              (item) => item.node,
            ),
          };
        },
      )
      .addMatcher(
        glTransactionsListApi.endpoints.deleteGLDocuments.matchFulfilled,
        (
          state,
          action: {
            meta: {
              arg: {
                originalArgs: {
                  body: {
                    ids: string[];
                  };
                };
              };
            };
          },
        ) => {
          const { ids } = action.meta.arg.originalArgs.body;

          const nextDocuments = state?.documents?.filter((item) =>
            ids.every((id) => item.id !== id),
          );

          state.document = undefined;

          state.documents = nextDocuments;
        },
      );
    builder.addMatcher(
      transactionApi.endpoints.addComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };
          let nextComments: DocumentComment[] = [];

          if (
            nextDocument.commentsList &&
            nextDocument.commentsList.length > 0
          ) {
            nextComments = ([] as DocumentComment[]).concat(
              nextDocument.commentsList,
            );
          }

          nextComments.push(action.payload.data);

          nextDocument.commentsList = nextComments;

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.editComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };
          nextDocument.commentsList = nextDocument.commentsList?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.changeDocumentStatus.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  status: DocumentStatus;
                };
              };
            };
          };
        },
      ) => {
        const { ids, status } = action.meta.arg.originalArgs.body;

        const nextDocuments = state?.documents?.map((item) => {
          const result = { ...item };
          const currentDocumentId = ids.find((id) => result.id === id);

          if (result.id === currentDocumentId) {
            result.status = status;
          }

          return result;
        });

        if (state.document) {
          const nextDocument = { ...state.document };

          if (ids.find((item) => item === nextDocument.id)) {
            nextDocument.status = status;

            state.document = nextDocument;
          }
        }

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.changeDocumentCategory.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  category: DocumentType;
                };
              };
            };
          };
        },
      ) => {
        const { ids, category } = action.meta.arg.originalArgs.body;

        const nextDocuments = state?.documents?.map((item) => {
          const result = { ...item };
          const currentDocumentId = ids.find((id) => result.id === id);

          if (result.id === currentDocumentId) {
            result.category = category;
          }

          return result;
        });

        if (state.document) {
          const nextDocument = { ...state.document };

          if (ids.find((item) => item === nextDocument.id)) {
            nextDocument.category = category;

            state.document = nextDocument;
          }
        }

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.deleteComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };

          nextDocument.commentsList = nextDocument?.commentsList?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          state.document = nextDocument;
        }
      },
    );
  },
});

export const {
  changePanelState,
  clearAccountBalance,
  clearGeneralLedgerTransactionState,
  clearDocumentState,
} = generalLedgerTransactionSlice.actions;

export default generalLedgerTransactionSlice.reducer;
